import { createSelector } from '@reduxjs/toolkit'
import type { UserAccountAttributeKey } from '~/@types/backend-types'
import { inArray } from '~/utilities/arrayUtils'

import type { StateWithJobInfo } from '../jobInfo/reducer'
import { getCreatorOfJob, getJobInformation } from '../jobInfo/selectors'
import type { User } from '../users/reducer'

import { accountAttributeAdapter, type StateWithAccountAttributes } from './accountAttributesSlice'
import type { MissingNameModalContext } from './actions'
import { LoginProgressStep, type StateWithCurrentUser } from './reducer'

const getCurrentUserState = (state: StateWithCurrentUser) => state.currentUser

export const getCurrentUserAccountInfo = createSelector(
    getCurrentUserState,
    (currentUserState) => currentUserState.accountInfo,
)

export const getUserLoginStatus = createSelector(
    getCurrentUserState,
    (currentUserState) => currentUserState.loginStatus,
)
const getIsUploadingProfilePhoto = createSelector(
    getCurrentUserState,
    (currentUserState) => currentUserState.isUploadingProfilePhoto,
)

export const isLoggedIn = createSelector(
    getCurrentUserState,
    (currentUserState) => currentUserState.loginStatus === LoginProgressStep.AUTHENTICATED,
)
export const isLoggingIn = createSelector(
    getCurrentUserState,
    (currentUserState) => currentUserState.loginStatus === LoginProgressStep.FETCHING,
)

export const getUsedStorage = createSelector(getCurrentUserAccountInfo, (accountInfo) => {
    if (accountInfo) {
        return accountInfo.used_space
    }
    return 0
})

export const getMaxStorage = createSelector(getCurrentUserAccountInfo, (accountInfo) => {
    if (accountInfo) {
        return accountInfo.hasUnlimitedQuota ? Infinity : accountInfo.max_space
    }
    return 0
})

export const hasCurrentUserUnlimitedStorage = createSelector(
    getCurrentUserAccountInfo,
    (accountInfo) => accountInfo?.hasUnlimitedQuota,
)

export const hasUserNotMediaFiles = createSelector(
    getCurrentUserAccountInfo,
    (accountInfo) => accountInfo?.has_files_not_media,
)

export const getUserHasGrants = createSelector(getCurrentUserAccountInfo, (accountInfo) =>
    Boolean(accountInfo?.max_space),
)

export const getUnusedStorage = createSelector(
    getMaxStorage,
    getUsedStorage,
    (max, used) => max - used,
)

export const isOutOfStorage = createSelector(
    getCurrentUserAccountInfo,
    getMaxStorage,
    getUsedStorage,
    (accountInfo, max, used) => accountInfo !== undefined && used >= max,
)

export const getUsedStorageRatio = createSelector(
    getUsedStorage,
    getMaxStorage,
    (used, max) => used / max,
)

export const isTNNCustomer = createSelector(getMaxStorage, (maxStorage) => {
    if (maxStorage > 0) {
        return maxStorage === Infinity
    }
})
export const getConnectedDevices = createSelector(
    getCurrentUserState,
    (currentUserState) => currentUserState.connectedDevices,
)

export const getCurrentUserUUID = createSelector(
    getCurrentUserAccountInfo,
    (accountInfo) => accountInfo?.uuid,
)

export const getEmailAdressOfLoggedInUser = createSelector(
    getCurrentUserAccountInfo,
    (accountInfo) => accountInfo?.logged_in_as,
)

export const getNameOfLoggedInUser = createSelector(
    getCurrentUserAccountInfo,
    (accountInfo) => accountInfo?.name,
)

export const mustProvideUserInfo = createSelector(
    getCurrentUserState,
    isLoggedIn,
    (currentUserState, isUserLoggedIn) =>
        currentUserState.userInfoRequired.isRequired && !isUserLoggedIn,
)

export const getProvideUserInfoReason = createSelector(
    getCurrentUserState,
    (currentUserState) => currentUserState.userInfoRequired.reason,
)

export const getTOSVerificationInfo = createSelector(
    getCurrentUserAccountInfo,
    (accountInfo) => accountInfo?.tos_to_approve,
)

export const isJobCreatedByCurrentUser = (
    state: StateWithJobInfo & StateWithCurrentUser,
    jobID: JobID,
): boolean | undefined => {
    const info = getJobInformation(state, jobID)
    if (info && info.type === 'timeline') {
        return true
    }
    const creator = getCreatorOfJob(state, jobID)
    if (creator !== undefined) {
        return creator === getCurrentUserUUID(state)
    }
}

export const getSubscribedJobs = (state: StateWithCurrentUser): JobID[] =>
    state.currentUser.subscribedJobs

export const isAlbumListReady = (state: StateWithCurrentUser): boolean =>
    state.currentUser.haveFetchedAlbumList

export const isCurrentUserSubscribedToJob = (
    state: StateWithJobInfo & StateWithCurrentUser,
    job: JobID,
): boolean => {
    const isOwnJob = isJobCreatedByCurrentUser(state, job)
    const subscribedJobs = getSubscribedJobs(state)

    return isOwnJob || inArray(subscribedJobs, job)
}

export type CurrentUserInfo = User & { isUploadingProfilePhoto: boolean }
export const getCurrentUserInfo = createSelector(
    getCurrentUserAccountInfo,
    getIsUploadingProfilePhoto,
    (accountInfo, isUploadingProfilePhoto) => {
        if (accountInfo) {
            return {
                userID: accountInfo.uuid,
                name: accountInfo.name,
                profilePicture: accountInfo.profile_picture,
                isUploadingProfilePhoto,
            }
        }
    },
)

export type FileTypeCount = {
    picture_count: number
    video_count: number
    document_count?: number
}
export const getFileCount = createSelector(getCurrentUserAccountInfo, (accountInfo) => {
    if (accountInfo) {
        const total_picture_count =
            accountInfo.file_type_counters.picture_count +
            accountInfo.file_type_counters.screenshot_count
        return {
            picture_count: total_picture_count,
            video_count: accountInfo.file_type_counters.video_count,
            document_count: accountInfo.file_type_counters.document_count,
        }
    }
})

export const getAlbumCount = createSelector(
    getCurrentUserAccountInfo,
    (accountInfo) => accountInfo?.number_of_subscribed_albums ?? 0,
)

export const isCurrentUserBeingShutDown = createSelector(
    getCurrentUserAccountInfo,
    (accountInfo) => {
        return accountInfo !== undefined && accountInfo.isBeingShutDown === true
    },
)

export type VersionNote = {
    header: string
    notes: string[]
}

export const getMissingNameModalVisibility = (state: StateWithCurrentUser): boolean => {
    return state.currentUser.missingNameModal.isVisible
}
export const getMissingNameModalContext = (
    state: StateWithCurrentUser,
): MissingNameModalContext => {
    return state.currentUser.missingNameModal.context
}

export const getIsReadOnlyUser = createSelector(
    getCurrentUserAccountInfo,
    (accountInfo) => accountInfo?.is_read_only_user ?? false,
)

const accountAttributeSelectors = accountAttributeAdapter.getSelectors()
export const getAccountAttributes = (state: StateWithAccountAttributes) =>
    accountAttributeSelectors.selectAll(state.accountAttributes.attributes)
export const getAccountAttribute = (
    state: StateWithAccountAttributes,
    id: UserAccountAttributeKey,
) => accountAttributeSelectors.selectById(state.accountAttributes.attributes, id)
export const getAccountAttributesStatus = (state: StateWithAccountAttributes) =>
    state.accountAttributes.status

export const getSunsetDeletionDate = createSelector(
    getCurrentUserAccountInfo,
    (accountInfo) => accountInfo?.sunset_delete_date,
)
